import { useState, useEffect, useRef } from 'react';
import { ScriptStatusEnum } from '@hooks/use-script/ScriptStatusEnum';
import { ScriptOptions } from '@hooks/use-script/ScriptOptions';



export const useScript = (src: string, options?: ScriptOptions) => {
	const preventLoad = options ? options.preventLoad : false;
	const removeOnUnmount = options ? options.removeOnUnmount : true;
	

	const cachedScriptStatus = useRef<Record<string, ScriptStatusEnum>>(null);
	
	const [ status, setStatus ] = useState<ScriptStatusEnum | undefined>(() =>
		(!src || preventLoad)
		? ScriptStatusEnum.idle
		: (typeof window === 'undefined' || typeof window.document === 'undefined')
		? ScriptStatusEnum.loading
		: cachedScriptStatus && cachedScriptStatus.current ? cachedScriptStatus.current[src] : ScriptStatusEnum.loading
	);

	
	useEffect(() => {
		if (!src || preventLoad || typeof window === 'undefined' || typeof window.document === 'undefined') {
			return;
		}

		const actualCachedScriptStatus = cachedScriptStatus && cachedScriptStatus.current ? cachedScriptStatus.current[src] : null;
		if (actualCachedScriptStatus === ScriptStatusEnum.ready || actualCachedScriptStatus === ScriptStatusEnum.error) {
			setStatus(actualCachedScriptStatus)
			return;
		}

		let scriptNode: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);

		if (!scriptNode) {
			scriptNode = document.createElement('script')
			scriptNode.src = src
			scriptNode.async = true
			scriptNode.setAttribute('data-status', ScriptStatusEnum.loading)
			document.body.appendChild(scriptNode)

			const setAttributeFromEvent = (event: Event) => {
				scriptNode?.setAttribute('data-status', event.type === 'load' ? ScriptStatusEnum.ready : ScriptStatusEnum.error)
			}

			scriptNode.addEventListener('load', setAttributeFromEvent)
			scriptNode.addEventListener('error', setAttributeFromEvent)
		} else {
			const scriptStatus: ScriptStatusEnum | null = scriptNode ? scriptNode.getAttribute('data-status') as ScriptStatusEnum : null;
			setStatus(scriptStatus ?? actualCachedScriptStatus ?? ScriptStatusEnum.loading);
		}

		const setStateFromEvent = (event: Event) => {
			const newStatus = event.type === 'load' ? ScriptStatusEnum.ready : ScriptStatusEnum.error;
			setStatus(newStatus)
			cachedScriptStatus.current = { [src]: newStatus };
		}

		scriptNode.addEventListener('load', setStateFromEvent)
		scriptNode.addEventListener('error', setStateFromEvent)

		return () => {
			if (scriptNode) {
				scriptNode.removeEventListener('load', setStateFromEvent)
				scriptNode.removeEventListener('error', setStateFromEvent)
			}
			if (scriptNode && removeOnUnmount) {
				scriptNode.remove()
			}
		}
	}, [ src, preventLoad, removeOnUnmount ]);

	return status;
};